import { createElement } from "react";
import { createRoot, type Root } from "react-dom/client";
import Logger from "@/lib/logger.ts";
import App from "./app.tsx";
import InvalidAttributeException from "./exceptions/InvalidAttributeException.ts";
import MissingAttributesException from "./exceptions/MissingAttributesException.ts";
import {
    ConfigurationTab,
    type ConfigurationTabEnum,
    type ModelCode,
    type SmartNumber,
} from "./features/sub-families/types";
import {
    type Locale,
    type Navigation,
    type Nullable,
    Site,
    type SiteEnum,
    WebComponentMode,
    type WebComponentModeEnum,
} from "./types";
import { getTypesAsString } from "./utils/formatters.ts";
import { isConfigurationTab, isLocale, isSite, isWebComponentMode } from "./utils/types.ts";

const SMART_NUMBER = "smart-number" as const;
const MODEL_CODE = "model-code" as const;
const WEB_COMPONENT_MODE = "mode" as const;
const CONFIGURATION_TAB = "tab" as const;
const PROPERTY = "property" as const;
const NAVIGATION = "navigation" as const;
const USER_INPUT = "user-input" as const;
const SITE = "site" as const;
const LOCALE = "locale" as const;
const API_TOKEN_URL = "api-token-url" as const;

export default class Deltalight3dConfigurator extends HTMLElement {
    #reactRoot: Nullable<Root> = null;

    #smartNumber: Nullable<SmartNumber> = null;
    #userInput: Nullable<SmartNumber> = null;
    #modelCode: Nullable<ModelCode> = null;
    #mode: Nullable<WebComponentModeEnum> = null;
    #configurationTab: Nullable<ConfigurationTabEnum> = null;
    #property: Nullable<string> = null;
    #navigation: Nullable<Navigation> = null;
    #site: SiteEnum = Site.Default;
    #locale: Locale = "en_BE";
    #apiTokenUrl: Nullable<string> = null;

    constructor() {
        super();
    }

    // region Custom Attributes
    static observedAttributes = [
        SMART_NUMBER,
        MODEL_CODE,
        WEB_COMPONENT_MODE,
        CONFIGURATION_TAB,
        PROPERTY,
        SITE,
        LOCALE,
        USER_INPUT,
        API_TOKEN_URL,
    ];

    get smartNumber() {
        return this.#smartNumber;
    }

    get modelCode() {
        return this.#modelCode;
    }

    get mode() {
        return this.#mode;
    }

    get configurationTab() {
        return this.#configurationTab;
    }

    get property() {
        return this.#property;
    }

    get site() {
        return this.#site;
    }

    get locale() {
        return this.#locale;
    }

    get userInput() {
        return this.#userInput;
    }

    get apiTokenUrl() {
        return this.#apiTokenUrl;
    }

    set smartNumber(value: Nullable<string>) {
        if (!value) this.removeAttribute(SMART_NUMBER);
        else this.setAttribute(SMART_NUMBER, value);
    }

    set modelCode(value: Nullable<string>) {
        if (!value) this.removeAttribute(MODEL_CODE);
        else this.setAttribute(MODEL_CODE, value);
    }

    set mode(value: Nullable<string>) {
        if (!isWebComponentMode(value)) this.removeAttribute(WEB_COMPONENT_MODE);
        else this.setAttribute(WEB_COMPONENT_MODE, value);
    }

    set configurationTab(value: Nullable<string>) {
        if (!value) this.removeAttribute(CONFIGURATION_TAB);
        else this.setAttribute(CONFIGURATION_TAB, value);
    }

    set property(value: Nullable<string>) {
        if (!value) this.removeAttribute(PROPERTY);
        else this.setAttribute(PROPERTY, value);
    }

    set site(value: Nullable<string>) {
        if (!isSite(value)) this.removeAttribute(SITE);
        else this.setAttribute(SITE, value);
    }

    set locale(value: Nullable<string>) {
        if (!value || !isLocale(value)) this.removeAttribute(LOCALE);
        else this.setAttribute(LOCALE, value);
    }

    set userInput(value: Nullable<string>) {
        if (!value) this.removeAttribute(USER_INPUT);
        else this.setAttribute(USER_INPUT, value);
    }

    attributeChangedCallback(name: string, oldValue: Nullable<string>, newValue: Nullable<string>) {
        if (newValue === oldValue) return;

        switch (name) {
            case SMART_NUMBER:
                this.smartNumber = newValue;
                break;

            case MODEL_CODE:
                this.modelCode = newValue;
                break;

            case WEB_COMPONENT_MODE:
                this.mode = newValue;
                break;

            case CONFIGURATION_TAB:
                this.configurationTab = newValue;
                break;

            case PROPERTY:
                this.property = newValue;
                break;

            case SITE:
                this.site = newValue;
                break;

            case LOCALE:
                this.locale = newValue;
                break;

            case USER_INPUT:
                this.userInput = newValue;
                break;

            case API_TOKEN_URL:
                this.#apiTokenUrl = newValue || null;
                break;
        }

        // Don't render if we are not connected yet!
        if (!this.#reactRoot) return;

        this.#render();
    }
    // endregion

    connectedCallback() {
        const shadow = this.shadowRoot ?? this.attachShadow({ mode: "open" });
        this.#reactRoot = createRoot(shadow);

        this.#render();
    }

    // region React Rendering
    disconnectedCallback() {
        this.#unmount();
    }

    #setProperties() {
        const { smartNumber, modelCode } = fixupSmartNumberAndModelCode(
            this.getAttribute(SMART_NUMBER),
            this.getAttribute(MODEL_CODE),
        );

        const { site, locale } = getWithDefaults(
            {
                site: this.getAttribute(SITE),
                locale: this.getAttribute(LOCALE),
            },
            { site: this.#site, locale: this.#locale },
        );

        this.#smartNumber = smartNumber;
        this.#modelCode = modelCode;
        this.#mode = getWebComponentLocation(this.getAttribute(WEB_COMPONENT_MODE));
        this.#configurationTab = getConfigurationTab(this.getAttribute(CONFIGURATION_TAB));
        this.#property = getValidProperty(this.getAttribute(PROPERTY));
        this.#navigation = getNavigation(this.getAttribute(NAVIGATION));
        this.#userInput = getValidUserInput(this.getAttribute(USER_INPUT));

        this.#site = site;
        this.#locale = locale;
        this.#apiTokenUrl = this.getAttribute(API_TOKEN_URL) || null;
    }

    #render() {
        this.#setProperties();

        if (!this.#mode) return null;

        return this.#reactRoot?.render(
            createElement(App, {
                smartNumber: this.#smartNumber,
                userInput: this.#userInput,
                modelCode: this.#modelCode,
                mode: this.#mode,
                configurationTab: this.#configurationTab,
                property: this.#property,
                navigation: this.#navigation,
                site: this.#site,
                locale: this.#locale,
                apiTokenUrl: this.#apiTokenUrl,
                host: this,
            }),
        );
    }

    #unmount() {
        this.#reactRoot?.unmount();
        this.#reactRoot = null;
    }
    // endregion
}

if (!customElements.get("deltalight-3d-configurator")) {
    customElements.define("deltalight-3d-configurator", Deltalight3dConfigurator);
}

declare global {
    interface HTMLElementTagNameMap {
        "deltalight-3d-configurator": Deltalight3dConfigurator;
    }
}

// region Helpers
function fixupSmartNumberAndModelCode(smartNumber: Nullable<string>, modelCode: Nullable<string>) {
    if (!smartNumber && !modelCode) throw new MissingAttributesException(1, SMART_NUMBER, MODEL_CODE);

    return { smartNumber: getSmartNumber(smartNumber), modelCode: getModelCode(modelCode) };
}

function getSmartNumber(value: Nullable<string>) {
    return getValidProperty<SmartNumber>(value);
}

function getModelCode(value: Nullable<string>) {
    return getValidProperty<ModelCode>(value);
}

function getWebComponentLocation(value: Nullable<string>) {
    if (!isWebComponentMode(value)) {
        throwInvalidAttributeException(WebComponentMode, WEB_COMPONENT_MODE);
    }

    return value;
}

function getConfigurationTab(value: Nullable<string>) {
    if (value !== null && !isConfigurationTab(value)) {
        throwInvalidAttributeException(ConfigurationTab, CONFIGURATION_TAB);
    }

    return value;
}

function getNavigation(value: Nullable<string>): Nullable<Navigation> {
    if (!value) return null;

    try {
        return JSON.parse(value) as Navigation;
    } catch (err) {
        Logger.error("Failed to parse navigation JSON", err);
        return null;
    }
}

function getValidUserInput(value: Nullable<string>): Nullable<SmartNumber> {
    if (!value || !isValidSmartNumber(value)) return null;

    return value as SmartNumber;
}

function getValidProperty<T>(value: Nullable<string>): Nullable<T> {
    return (value || null) as Nullable<T>;
}

type Defaults = { site: SiteEnum; locale: Locale };
type Candidates = Record<keyof Defaults, Nullable<string>>;
function getWithDefaults(candidates: Candidates, defaults: Defaults) {
    const site = isSite(candidates.site) ? candidates.site : defaults.site;
    const locale = isLocale(candidates.locale) ? candidates.locale : defaults.locale;

    return { site, locale };
}

function throwInvalidAttributeException(validValues: Readonly<Record<string, string>>, attribute: string): never {
    const validTypes = getTypesAsString(...Object.values(validValues));
    throw new InvalidAttributeException(attribute, validTypes);
}

function isValidSmartNumber(value: string) {
    return new RegExp("^\\d{6}").test(value);
}
// endregion
